import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  LinearProgress,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import {
  Security as SecurityIcon,
  Fingerprint as FingerprintIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import api from '../../utils/api';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  webAuthnEnabled: boolean;
  totpEnabled: boolean;
  lastLogin?: string;
  createdAt: string;
}

interface WebAuthnCredential {
  id: string;
  name: string;
  createdAt: string;
  lastUsed?: string;
  transports?: string[];
  credentialDeviceType: 'singleDevice' | 'multiDevice';
}

interface MfaStats {
  totalUsers: number;
  mfaEnabledUsers: number;
  adoptionRate: number;
  recentRegistrations: number;
  credentialsByType: {
    platform: number;
    crossPlatform: number;
  };
}

function MfaManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<MfaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [mfaFilter, setMfaFilter] = useState('all');
  const [sortField, setSortField] = useState<'name' | 'role' | 'mfaStatus' | 'lastLogin'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userCredentials, setUserCredentials] = useState<WebAuthnCredential[]>([]);
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Load users first, then stats (so fallback calculation has user data)
      const loadedUsers = await loadUsers();
      await loadStats(loadedUsers);
    } catch (error) {
      console.error('Error loading MFA data:', error);
      setError('Failed to load MFA management data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadUsers = async (): Promise<User[]> => {
    try {
      const response = await api.get('/api/users');
      // API returns users array directly, not wrapped in a users property
      const usersData = Array.isArray(response.data) ? response.data : (response.data.users || []);
      setUsers(usersData);
      return usersData;
    } catch (error) {
      console.error('Error loading users:', error);
      return [];
    }
  };

  const loadStats = async (loadedUsers: User[]) => {
    try {
      const response = await api.get('/api/admin/mfa-stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error loading MFA stats:', error);
      // Create fallback stats using loaded users data
      const mfaUsers = loadedUsers.filter(u => u.webAuthnEnabled || u.totpEnabled).length;
      setStats({
        totalUsers: loadedUsers.length,
        mfaEnabledUsers: mfaUsers,
        adoptionRate: loadedUsers.length > 0 ? (mfaUsers / loadedUsers.length) * 100 : 0,
        recentRegistrations: 0,
        credentialsByType: {
          platform: 0,
          crossPlatform: 0
        }
      });
    }
  };

  const loadUserCredentials = async (userId: string) => {
    try {
      const response = await api.get(`/api/admin/users/${userId}/webauthn-credentials`);
      if (response.data.success) {
        setUserCredentials(response.data.credentials);
      }
    } catch (error) {
      console.error('Error loading user credentials:', error);
      setError('Failed to load user credentials');
    }
  };

  const handleViewCredentials = async (user: User) => {
    setSelectedUser(user);
    setCredentialsDialog(true);
    await loadUserCredentials(user._id);
  };

  const handleRemoveCredential = async (credentialId: string) => {
    if (!selectedUser) return;

    try {
      const response = await api.delete(`/api/admin/users/${selectedUser._id}/webauthn-credentials/${credentialId}`);
      if (response.data.success) {
        setSuccess('Credential removed successfully');
        await loadUserCredentials(selectedUser._id);
        await loadData(); // Refresh stats
      }
    } catch (error) {
      console.error('Error removing credential:', error);
      setError('Failed to remove credential');
    }
  };

  const handleDisableMfa = async (userId: string) => {
    try {
      const response = await api.post(`/api/admin/users/${userId}/disable-mfa`);
      if (response.data.success) {
        setSuccess('MFA disabled for user');
        await loadData();
        setCredentialsDialog(false);
      }
    } catch (error) {
      console.error('Error disabling MFA:', error);
      setError('Failed to disable MFA');
    }
  };

  const handleResetTotp = async (userId: string) => {
    try {
      const response = await api.post(`/api/admin/users/${userId}/reset-totp`);
      if (response.data.success) {
        setSuccess('TOTP reset successfully. User will need to set up authenticator app again on next login.');
        await loadData();
        setCredentialsDialog(false);
      }
    } catch (error) {
      console.error('Error resetting TOTP:', error);
      setError('Failed to reset TOTP');
    }
  };

  const roles = Array.from(new Set(users.map(u => u.role).filter(Boolean))).sort();

  const filteredUsers = users.filter(user => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch = !term ||
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term);
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesMfa = mfaFilter === 'all' ||
      (mfaFilter === 'webauthn' && user.webAuthnEnabled) ||
      (mfaFilter === 'totp' && user.totpEnabled) ||
      (mfaFilter === 'disabled' && !user.webAuthnEnabled && !user.totpEnabled);
    return matchesSearch && matchesRole && matchesMfa;
  });

  const getMfaStatusLabel = (user: User): string =>
    user.webAuthnEnabled ? 'WebAuthn' : user.totpEnabled ? 'TOTP' : 'Disabled';

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'name':
        return `${a.firstName} ${a.lastName}`.toLowerCase()
          .localeCompare(`${b.firstName} ${b.lastName}`.toLowerCase()) * dir;
      case 'role':
        return a.role.localeCompare(b.role) * dir;
      case 'mfaStatus':
        return getMfaStatusLabel(a).localeCompare(getMfaStatusLabel(b)) * dir;
      case 'lastLogin': {
        const av = a.lastLogin ? new Date(a.lastLogin).getTime() : null;
        const bv = b.lastLogin ? new Date(b.lastLogin).getTime() : null;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;  // 'Never' always sorts last
        if (bv === null) return -1;
        return (av - bv) * dir;
      }
      default:
        return 0;
    }
  });

  const sortLabelSx = {
    color: 'rgba(255, 255, 255, 0.9) !important',
    '&:hover': { color: 'rgba(255, 255, 255, 1) !important' },
    '&.Mui-active': { color: 'rgba(100, 181, 246, 1) !important' },
    '& .MuiTableSortLabel-icon': { color: 'rgba(100, 181, 246, 0.9) !important' }
  };

  const getDeviceTypeIcon = (deviceType: string) => {
    return deviceType === 'multiDevice' ? <SecurityIcon /> : <FingerprintIcon />;
  };

  const getDeviceTypeLabel = (deviceType: string) => {
    return deviceType === 'multiDevice' ? 'Security Key' : 'Platform Authenticator';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Loading MFA Management...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom className="theme-text-primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShieldIcon />
        Multi-Factor Authentication Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* MFA Statistics */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon sx={{ color: 'rgba(100, 181, 246, 0.9)' }} />
                  <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{stats.totalUsers}</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Total Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon sx={{ color: 'rgba(76, 175, 80, 0.9)' }} />
                  <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{stats.mfaEnabledUsers}</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  MFA Enabled
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon sx={{ color: 'rgba(33, 150, 243, 0.9)' }} />
                  <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{stats.adoptionRate.toFixed(1)}%</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Adoption Rate
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.adoptionRate} 
                  sx={{ mt: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)', '& .MuiLinearProgress-bar': { backgroundColor: 'rgba(33, 150, 243, 0.9)' } }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {stats.adoptionRate < 50 ? <WarningIcon sx={{ color: 'rgba(255, 152, 0, 0.9)' }} /> : <SecurityIcon sx={{ color: 'rgba(76, 175, 80, 0.9)' }} />}
                  <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {stats.adoptionRate >= 80 ? 'Excellent' : 
                     stats.adoptionRate >= 50 ? 'Good' : 'Needs Improvement'}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Security Status
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* User Search & Filters */}
      <Card sx={{ mb: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                flex: 1,
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                },
                '& .MuiInputBase-input::placeholder': { color: 'rgba(255, 255, 255, 0.5)', opacity: 1 }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl
              size="small"
              sx={{
                minWidth: 160,
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(100, 181, 246, 0.8)' },
                '& .MuiOutlinedInput-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                },
                '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
              }}
            >
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(e) => setRoleFilter(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(30, 41, 59, 0.98)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiMenuItem-root': {
                        color: 'rgba(255, 255, 255, 0.9)',
                        textTransform: 'capitalize',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                        '&.Mui-selected': { backgroundColor: 'rgba(100, 181, 246, 0.2)' }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="all">All Roles</MenuItem>
                {roles.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{
                minWidth: 160,
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(100, 181, 246, 0.8)' },
                '& .MuiOutlinedInput-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(100, 181, 246, 0.5)' }
                },
                '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
              }}
            >
              <InputLabel>MFA Status</InputLabel>
              <Select
                value={mfaFilter}
                label="MFA Status"
                onChange={(e) => setMfaFilter(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(30, 41, 59, 0.98)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiMenuItem-root': {
                        color: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                        '&.Mui-selected': { backgroundColor: 'rgba(100, 181, 246, 0.2)' }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="webauthn">WebAuthn</MenuItem>
                <MenuItem value="totp">TOTP</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom className="theme-text-primary">
            User MFA Status ({filteredUsers.length} users)
          </Typography>
          
          <TableContainer component={Paper} sx={{ backgroundColor: 'transparent', backgroundImage: 'none' }}>
            <Table>
              <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                      sx={sortLabelSx}
                    >
                      User
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <TableSortLabel
                      active={sortField === 'role'}
                      direction={sortField === 'role' ? sortDirection : 'asc'}
                      onClick={() => handleSort('role')}
                      sx={sortLabelSx}
                    >
                      Role
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <TableSortLabel
                      active={sortField === 'mfaStatus'}
                      direction={sortField === 'mfaStatus' ? sortDirection : 'asc'}
                      onClick={() => handleSort('mfaStatus')}
                      sx={sortLabelSx}
                    >
                      MFA Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <TableSortLabel
                      active={sortField === 'lastLogin'}
                      direction={sortField === 'lastLogin' ? sortDirection : 'asc'}
                      onClick={() => handleSort('lastLogin')}
                      sx={sortLabelSx}
                    >
                      Last Login
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUsers.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                          {user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Chip 
                        label={user.role} 
                        size="small"
                        color={user.role === 'administrator' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Chip
                        icon={(user.webAuthnEnabled || user.totpEnabled) ? <SecurityIcon /> : <WarningIcon />}
                        label={
                          user.webAuthnEnabled ? 'WebAuthn' :
                          user.totpEnabled ? 'TOTP' :
                          'Disabled'
                        }
                        color={(user.webAuthnEnabled || user.totpEnabled) ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {user.lastLogin 
                          ? new Date(user.lastLogin).toLocaleDateString()
                          : 'Never'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Tooltip title="View Credentials">
                        <IconButton
                          size="small"
                          onClick={() => handleViewCredentials(user)}
                          disabled={!user.webAuthnEnabled && !user.totpEnabled}
                          sx={{ color: 'rgba(100, 181, 246, 0.9)', '&:hover': { color: 'rgba(100, 181, 246, 1)' }, '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' } }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* User Credentials Dialog */}
      <Dialog 
        open={credentialsDialog} 
        onClose={() => setCredentialsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          MFA Credentials for {selectedUser?.firstName} {selectedUser?.lastName}
        </DialogTitle>
        <DialogContent>
          {/* Show MFA Methods Summary */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Enabled MFA Methods:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {selectedUser?.webAuthnEnabled && (
                <Chip icon={<SecurityIcon />} label="WebAuthn" color="primary" size="small" />
              )}
              {selectedUser?.totpEnabled && (
                <Chip icon={<SecurityIcon />} label="TOTP (Authenticator App)" color="primary" size="small" />
              )}
              {!selectedUser?.webAuthnEnabled && !selectedUser?.totpEnabled && (
                <Chip icon={<WarningIcon />} label="No MFA Enabled" color="warning" size="small" />
              )}
            </Box>
          </Box>

          {userCredentials.length === 0 && !selectedUser?.totpEnabled ? (
            <Alert severity="info">
              This user has no registered MFA credentials.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userCredentials.map((credential) => (
                    <TableRow key={credential.id}>
                      <TableCell>{credential.name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getDeviceTypeIcon(credential.credentialDeviceType)}
                          <Typography variant="body2">
                            {getDeviceTypeLabel(credential.credentialDeviceType)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(credential.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {credential.lastUsed 
                          ? new Date(credential.lastUsed).toLocaleDateString()
                          : 'Never'
                        }
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remove Credential">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveCredential(credential.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          {selectedUser?.totpEnabled && (
            <Button
              color="warning"
              onClick={() => handleResetTotp(selectedUser._id)}
              startIcon={<LockResetIcon />}
            >
              Reset TOTP
            </Button>
          )}
          {(selectedUser?.webAuthnEnabled || selectedUser?.totpEnabled) && (
            <Button
              color="error"
              onClick={() => handleDisableMfa(selectedUser._id)}
            >
              Disable All MFA
            </Button>
          )}
          <Button onClick={() => setCredentialsDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MfaManagement;
